import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { initializeTheme } from './stores/themeStore'

/**
 * Conditionally start MSW (Mock Service Worker) for local development.
 * Enable by setting VITE_ENABLE_MOCKS=true in your .env file.
 * When enabled, all API requests are intercepted and served with mock data —
 * no backend required.
 */
async function enableMocking(): Promise<void> {
  if (import.meta.env.VITE_ENABLE_MOCKS !== 'true') {
    return
  }

  const { worker } = await import('./mocks/browser')

  await worker.start({
    onUnhandledRequest: 'bypass', // Let unhandled requests pass through
  })

  console.log(
    '%c[MSW] Mock Service Worker enabled — API requests are being intercepted',
    'color: #10b981; font-weight: bold;'
  )
}

/**
 * Conditionally set up auto-authentication for mock mode.
 * When VITE_MOCK_API=true, pre-populates localStorage with mock tokens
 * and user/tenant data so the app boots into an authenticated state.
 */
async function enableAutoAuth(): Promise<void> {
  if (import.meta.env.VITE_MOCK_API !== 'true') {
    return
  }

  const { setupAutoAuth } = await import('./mocks/auto-auth')
  setupAutoAuth()
}

// Initialize mocks, then auto-auth, then theme, then render
enableMocking().then(() => enableAutoAuth()).then(() => {
  // Initialize theme before rendering to prevent flash of unstyled content
  initializeTheme()

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
})
